import React, { useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { IconButton } from '@material-ui/core';
import { Star } from '@material-ui/icons';
import {
  get, includes, isEmpty, join,
} from 'lodash/fp';
import dayjs from 'dayjs';

const S = {
  Wrapper: styled.div`
    position: relative;
    padding: 16px;
    background-color: ${({ theme }) => theme.palette.primary.contrastText};
  `,
  NewsletterTitle: styled.h3`
    width: 80%;
    margin-bottom: 0.25rem;
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: 1rem;
    line-height: 1.5;
  `,
  Target: styled.p`
    font-size: 0.75rem;
    line-height: 1.5;
    color: ${({ theme }) => theme.palette.grey[600]};
  `,
  Date: styled.time`
    display: block;
    margin-bottom: 1rem;
    font-size: 0.75rem;
    line-height: 1.5;
    color: ${({ theme }) => theme.palette.grey[600]};
  `,
  InfoItem: styled.li`
    display: flex;
    :not(:last-child) {
      margin-bottom: 4px;
    }
  `,
  InfoLabel: styled.p`
    margin-right: 0.75rem;
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    line-height: 1.62;
  `,
  InfoValue: styled.p`
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    line-height: 1.62;
    ${({ active, recorded, theme }) => active && recorded === true && css`
      color: ${theme.palette.status.info};
    `}
    ${({ active, recorded, theme }) => active && recorded === false && css`
      color: ${theme.palette.status.error.main};
    `}
  `,
  IconButton: styled(IconButton)`
    position: absolute;
    top: ${({ theme }) => theme.typography.pxToRem(13)};
    right: ${({ theme }) => theme.typography.pxToRem(13)};
  `,
  Star: styled(({ active, ...otherProps }) => <Star {...otherProps} />)`
    color: ${({ theme }) => theme.palette.grey[400]};
    font-size: 1.25rem;
    ${({ active, theme }) => active && css`
      color: ${theme.palette.secondary.main};
    `}
  `,
};

const Information = ({
  className, favorites, hasNoSurvey, newsletter, target, onUpdate,
}) => {
  const renderStatus = useCallback(() => {
    if (get('status', newsletter.data) === 'ongoing') {
      return (get(`targets[${get('id', target.data)}]`, newsletter.data) || hasNoSurvey)
        ? '응답완료'
        : '미응답';
    }

    return '응답마감';
  }, [hasNoSurvey, newsletter.data, target.data]);

  return (
    <S.Wrapper className={className}>
      <S.NewsletterTitle>
        {get('title', newsletter.data)}
      </S.NewsletterTitle>
      <S.Target>
        {join('', [
          get('schoolName', newsletter.data) ? `${get('schoolName', newsletter.data)} ` : '',
          get('contact.class_group', target.data)
            ? `${get('contact.class_group.grade', target.data)}학년 ${get('contact.class_group.class_name', target.data)}반 `
            : `${isEmpty(get('contact.custom_groups', target.data)) ? '미지정' : get('contact.custom_groups[0].name', target.data)}`,
          get('contact.name', target.data),
        ])}
      </S.Target>
      <S.Date dateTime={dayjs(get('sendAt', newsletter.data)).format('YYYY.MM.DD. HH:mm')}>
        {dayjs(get('sendAt', newsletter.data)).format('YYYY.MM.DD. HH:mm')}
      </S.Date>
      <ul>
        <S.InfoItem>
          <S.InfoLabel>
            대상
          </S.InfoLabel>
          <S.InfoValue>
            {`${get('name', target.data)}(${{
              mother: '모',
              father: '부',
              grand_mother: '조모',
              grand_father: '조부',
              etc: '기타',
            }[get('relationship', target.data)]})`}
          </S.InfoValue>
        </S.InfoItem>
        <S.InfoItem>
          <S.InfoLabel>
            상태
          </S.InfoLabel>
          <S.InfoValue
            active={get('status', newsletter.data) === 'ongoing'}
            recorded={get(`targets[${get('id', target.data)}]`, newsletter.data) || hasNoSurvey}
          >
            {renderStatus()}
          </S.InfoValue>
        </S.InfoItem>
        <S.InfoItem>
          <S.InfoLabel>
            설명
          </S.InfoLabel>
          <S.InfoValue>
            {get('description', newsletter.data)}
          </S.InfoValue>
        </S.InfoItem>
      </ul>
      <S.IconButton
        type="button"
        size="small"
        data-value={`${get('id', target.data)}_${get('id', newsletter.data)}`}
        onClick={onUpdate}
      >
        <S.Star active={includes(`${get('id', target.data)}_${get('id', newsletter.data)}`, favorites.data)} />
      </S.IconButton>
    </S.Wrapper>
  );
};

Information.propTypes = {
  favorites: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.string),
    loading: PropTypes.bool.isRequired,
    error: PropTypes.instanceOf(Error),
  }).isRequired,
  hasNoSurvey: PropTypes.bool,
  newsletter: PropTypes.shape({
    data: PropTypes.shape({
      blob: PropTypes.instanceOf(Blob).isRequired,
      description: PropTypes.string,
      sendAt: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
    }),
    loading: PropTypes.bool.isRequired,
    error: PropTypes.instanceOf(Error),
  }).isRequired,
  target: PropTypes.shape({
    data: PropTypes.shape({
      name: PropTypes.string.isRequired,
      relationship: PropTypes.oneOf([
        'mother',
        'father',
        'grand_mother',
        'grand_father',
        'etc',
      ]).isRequired,
      contact: PropTypes.shape({
        name: PropTypes.string.isRequired,
      }).isRequired,
    }),
    loading: PropTypes.bool.isRequired,
    error: PropTypes.instanceOf(Error),
  }).isRequired,
  className: PropTypes.string,
  onUpdate: PropTypes.func.isRequired,
};

Information.defaultProps = {
  className: undefined,
  hasNoSurvey: false,
};

export default memo(Information);
